// semeia-disable-reexport
import { assert } from "@ember/debug";
import type SurveyCompletedJournalEvent from "parog-web/models/survey-completed-journal-event";

import dayjs from "dayjs";
import type JournalEventModel from "parog-web/models/journal-event";
import { sortAsc, sortBy } from "parog-web/utils/sort";

function getLastJe<T extends JournalEventModel>(
  journalEvents: T[]
): T | undefined {
  return journalEvents.length > 0
    ? journalEvents.sort(sortBy("occurredAt", "desc"))[0]
    : undefined;
}

function getJesSince<T extends JournalEventModel>(
  journalEvents: JournalEventModel[],
  date: Date,
  ordering: "desc" | "asc" = "desc"
): T[] {
  if (!journalEvents || !journalEvents.length) return [];

  return journalEvents
    .filter(je => je.occurredAt >= date)
    .sort(sortBy("occurredAt", ordering)) as T[];
}

function getJesSinceYear<T extends JournalEventModel>(
  journalEvents: T[],
  numberOfYear = 1,
  ordering: "desc" | "asc" = "desc"
): T[] {
  return getJesSince(
    journalEvents,
    dayjs().subtract(numberOfYear, "year").toDate(),
    ordering
  );
}

/**
 * Find the last JournalEvent which is an instance of the given Class.
 */
function getLastJeOfClass<T extends JournalEventModel>(
  journalEvents: JournalEventModel[],
  journalEventClass: { new (): T }
): T | undefined {
  return getJesOfClass(journalEvents, journalEventClass, "desc")[0];
}

/**
 * Find all the JournalEvents which are instances of the given Class.
 *
 * @example
 * import NotificationTriggeredJournalEventModel from "core/models/notification-triggered-journal-event";
 * const notifications = getJesOfClass( journalEvents, NotificationTriggeredJournalEventModel, "asc")
 */
function getJesOfClass<T extends JournalEventModel>(
  journalEvents: JournalEventModel[],
  journalEventClass: { new (): T },
  ordering: "desc" | "asc" = "desc"
): T[] {
  const filteredJEs = journalEvents.filter(je => {
    if (je.isArchived) return false;
    return je instanceof journalEventClass;
  }) as T[];

  return filteredJEs.sort(sortBy("occurredAt", ordering));
}

function getJesOfClassSince<T extends JournalEventModel>(
  journalEvents: JournalEventModel[],
  journalEventClass: { new (): T },
  date: Date,
  ordering: "desc" | "asc" = "desc"
): Array<T> {
  return getJesSince<T>(
    journalEvents.filter(je => je instanceof journalEventClass),
    date,
    ordering
  ) as T[];
}

function getJesOfClassSinceYear<T extends JournalEventModel>(
  journalEvents: JournalEventModel[],
  journalEventClass: { new (): T },
  numberOfYear = 1,
  ordering: "desc" | "asc" = "desc"
): Array<T> {
  const yearsAgo: Date = new Date();
  yearsAgo.setFullYear(new Date().getFullYear() - numberOfYear);

  return getJesOfClassSince<T>(
    journalEvents,
    journalEventClass,
    yearsAgo,
    ordering
  );
}

function getLastSurveyCompleted(
  surveyCompletedjournalEvents: SurveyCompletedJournalEvent[],
  formId: string
): SurveyCompletedJournalEvent | undefined {
  assert("The 'formId' should be provided", !!formId);

  return surveyCompletedjournalEvents
    .sort(sortBy("occurredAt", "desc"))
    .find(je => {
      return je.formId === formId;
    });
}

function groupAndSortJesByDate<T extends { occurredAt: Date }>(
  journalEvents: T[]
): T[][] {
  const groupedJes = new Map();

  journalEvents.forEach(je => {
    const occurredAt = dayjs(je.occurredAt).format("DD/MM/YYYY");

    if (!groupedJes.has(occurredAt)) {
      groupedJes.set(occurredAt, []);
    }

    groupedJes.get(occurredAt).push(je);
  });

  return Array.from(groupedJes.values()).sort((jesA, jesB) =>
    sortAsc(jesA[0], jesB[0])
  );
}

export {
  getJesOfClass,
  getJesOfClassSince,
  getJesOfClassSinceYear,
  getJesSince,
  getJesSinceYear,
  getLastJe,
  getLastJeOfClass,
  getLastSurveyCompleted,
  groupAndSortJesByDate
};
